import { Component, Input, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Order, OrderManagerService, OrderRepositoryService, OrderStateRepositoryService, eOrderType, ePartyType, Party, eOrderStatusType, eResultState, ValidationService, ConfigurationState, UltimateBeneficialOwnershipRequestService, UltimateBeneficialOwnershipDialogComponent, ConfirmDialogComponent, eIdentityVerificationSource, eIdentityVerificationResult, AttestorHistoryDialogComponent, AttestationTemplateRepositoryService, UboTreeNodesService, UboTreeNode, PatchDocument } from 'reg-hub-common';
import { ActivatedRoute, Router } from '@angular/router';
import { ActionUIConfiguration } from 'projects/reg-hub-common/src/lib/interfaces/ui-configurations/action-ui-configuration';
import { DuplicateInComponent } from '../duplicate-in/duplicate-in.component';
import { firstValueFrom, Subject, takeUntil } from 'rxjs';
import { ConfirmCancelComponent } from '../confirm-cancel/confirm-cancel.component';
import { ToastrService } from 'ngx-toastr';
import { LinkToHubReportDialogComponent } from '../link-to-hub-report-dialog/link-to-hub-report-dialog.component';
import { ResendMessageDialog } from '../resend-message-dialog/resend-message-dialog.component';
import { TranslocoService } from '@jsverse/transloco';

@Component({
  selector: 'app-actions',
  templateUrl: './actions.component.html',
  styleUrls: ['./actions.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class ActionsComponent implements OnInit, OnDestroy {
  private readonly transloco = inject(TranslocoService);
  @Input() order!: Order;
  @Input() uiConfiguration!: ActionUIConfiguration;
  @Input() uboTreeNode?: UboTreeNode;
  @Input() downstreamShareholderHasUbo: boolean = true; // defaulting to true to reduce screen flicker
  protected linkableUboTreeNodes?: UboTreeNode[];
  protected baseOrderDetails!: FormGroup;
  protected isSaving: boolean = false;
  protected isActionable: boolean = false;
  protected isValidForExternalErrorCorrection: boolean = false;
  private onDestroy$ = new Subject<void>();
  private hasUltimateBeneficialOwnershipRequest: boolean = false;

  constructor(
    private dialog: MatDialog,
    private formBuilder: FormBuilder,
    private ordersRepo: OrderRepositoryService,
    private orderManager: OrderManagerService,
    private orderStateRepo: OrderStateRepositoryService,
    private router: Router,
    private validationService: ValidationService,
    private route: ActivatedRoute,
    private configuration: ConfigurationState,
    private messageService: ToastrService,
    private uboTreeService: UboTreeNodesService,
    private ultimateBeneficialOwnershipRequestService: UltimateBeneficialOwnershipRequestService,
    private attestationTemplateRepositoryService: AttestationTemplateRepositoryService
  ) { }

  ngOnInit(): void {
    this.baseOrderDetails = this.formBuilder.group({
      referenceNumber: [this.order.referenceNumber, Validators.required],
      country: [this.order.country, Validators.required],
      jurisdiction: [this.order.jurisdiction, Validators.required],
      orderTypeID: [this.order.orderTypeID, Validators.required],
      originalOrderID: [this.order.id, Validators.required],
      orderGroupID: [this.order.orderGroupID]
    });

    this.configuration.getOrderTypeAccessLevels().then(accessLevels => {
      this.setIsActionable();
    });

    this.orderManager.currentOrder.pipe(
      takeUntil(this.onDestroy$))
      .subscribe(order => {
        this.order = order;
        this.setIsActionable();
        this.shouldShowExternalErrorCorrectionButton();

        // Gate the UBO/PEPs buttons: don't allow ordering another UBO (or PEPs &
        // Sanctions) off a business report that already has a non-cancelled UBO request.
        if (this.order.orderTypeID === eOrderType.businessReport) {
          this.ultimateBeneficialOwnershipRequestService.businessReportHasUltimateBeneficialOwnershipRequest(this.order.id)
            .subscribe(result => this.hasUltimateBeneficialOwnershipRequest = result.exists);
        }
      });

    this.uboTreeService.linkToHubReportNodesDistinctByHubReport.pipe(
      takeUntil(this.onDestroy$)
    ).subscribe(nodes => this.linkableUboTreeNodes = nodes);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  amend() {
    // replacer excludes properties with ID
    var copiedOrder = JSON.parse(JSON.stringify(this.order));
    copiedOrder.originalOrderID = this.order.id;
    copiedOrder.orderTypeID = eOrderType.amendment;
    copiedOrder.parties = copiedOrder.parties.filter((party: Party) => party.partyTypeID !== ePartyType.businessRegisteringAgent && party.partyTypeID !== ePartyType.individualRegisteringAgent);

    delete copiedOrder.id; // Remove ID from request

    this.requestOrder(eOrderType.amendment, copiedOrder);
  }

  renew() {
    const newOrderType = this.order.orderTypeID === eOrderType.basicRenewal ? eOrderType.basicRenewal : eOrderType.renewal;

    if (newOrderType === eOrderType.basicRenewal) {
      this.requestOrder(newOrderType, {
        referenceNumber: this.order.referenceNumber,
        jurisdiction: this.order.jurisdiction,
        country: this.order.country,
        orderTypeID: newOrderType,
        originalOrderID: this.order.id,
        lien: {}
      } as any);
    } else {
      this.requestOrder(newOrderType);
    }
  }

  discharge() {
    const newOrderType = this.order.orderTypeID === eOrderType.basicDischarge ||
      this.order.orderTypeID === eOrderType.basicRenewal ?
      eOrderType.basicDischarge :
      eOrderType.discharge;

    if (newOrderType === eOrderType.basicDischarge) {
      this.requestOrder(newOrderType, {
        referenceNumber: this.order.referenceNumber,
        jurisdiction: this.order.jurisdiction,
        country: this.order.country,
        orderTypeID: newOrderType,
        originalOrderID: this.order.id,
        lien: {}
      } as any);
    } else {
      this.requestOrder(newOrderType);
    }
  }

  requestPEPsAndSanctins() {
    const newOrderType = eOrderType.pepsAndSanctions;

    this.requestOrder(newOrderType);
  }

  reregister() {
    this.requestOrder(eOrderType.reregistration);
  }

  edit() {
    const newStatus = {
      newStatus: "HistoricalDraft",
      status: "",
      message: "Cancelled from Reg Hub Client"
    };

    this.orderStateRepo.updateOrderState(this.order.id, newStatus)
      .subscribe(result => {
        this.isSaving = false;
        this.router.navigate(['orders', 'ppsa-registration', this.order.id]);
      });
  }

  duplicate() {
    if (this.order.orderTypeID === eOrderType.businessSearch) {
      this.duplicateExact();
      return;
    }

    const dialogRef = this.dialog.open(DuplicateInComponent, {
      data: this.order,
      minWidth: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.event === 'submitted') {
        this.isSaving = true;
      }
    });
  }

  private duplicateExact() {
    this.isSaving = true;

    this.ordersRepo.duplicateOrder(this.order.id, this.order.jurisdiction)
      .subscribe({
        next: (result: any) => {
          this.isSaving = false;
          this.router.navigate(['orders', result.resource.id]);
        },
        error: () => this.isSaving = false
      });
  }

  updateBusinessReport() {
    if (this.order.orderTypeID === eOrderType.businessReport) {
      this.isSaving = true;
      const newOrder = JSON.parse(JSON.stringify(this.order));
      newOrder.orderGroupID = this.order.orderGroup?.id;

      delete (newOrder.id);
      delete (newOrder.added);

      const businessSearchResultID = this.route.snapshot.queryParamMap.get('businessSearchResultID');

      if (businessSearchResultID) {
        newOrder.businessSearchResultID = businessSearchResultID;
      }

      this.ordersRepo.createNewOrder(newOrder)
        .subscribe(result => {
          const originatingOrderID = this.order.id;
          this.isSaving = false;
          const order = (result as any).resource;
          this.order = order;
          this.orderManager.updateOrder(order);
          this.validationService.currentValidation$.next((result as any).validation);
          if (this.order.id) {
            this.router.navigate(['orders', 'business', order.id], {
              queryParams: { originatingOrderID: originatingOrderID }
            });
          }
        });
    }
  }

  showDuplicateButton(): boolean {
    if (this.order.orderTypeID === eOrderType.businessSearch) {
      return this.hasDraftAccess(this.order.orderTypeID) && (this.uiConfiguration.hasDuplicate ?? false) &&
        this.order.orderStatusTypeID === eOrderStatusType.complete;
    }

    return this.hasDraftAccess(this.order.orderTypeID) && (this.uiConfiguration.hasDuplicate ?? false) &&
      !(this.order.lien?.registrationResults?.some(res => res.expiryDate ? new Date() > res.expiryDate : false) ?? true) &&
      !this.isExpired();
  }

  showUpdateBusinessReportButton(): boolean {
    return this.hasDraftAccess(this.order.orderTypeID) && (this.uiConfiguration.hasUpdate ?? false) && this.order.searchResult?.resultState?.id !== eResultState.noResult && this.isActionable;
  }

  showUltimateBeneficialOwnershipReportButton(): boolean {
    return this.hasDraftAccess(eOrderType.ultimateBeneficialOwnershipReport) &&
      (this.uiConfiguration.hasUltimateBeneficialOwnershipReport ?? false) &&
      this.order.searchResult?.resultState?.id !== eResultState.noResult &&
      !this.hasUltimateBeneficialOwnershipRequest &&
      this.order.orderStatusTypeID !== eOrderStatusType.cancelled &&
      !this.downstreamShareholderHasUbo;
  }

  showPEPsAndSanctionsButton(): boolean {
    return this.hasDraftAccess(eOrderType.pepsAndSanctions) && (this.uiConfiguration.hasPEPsAndSanctions ?? false) && this.order.searchResult?.resultState?.id !== eResultState.noResult && !this.hasUltimateBeneficialOwnershipRequest && this.order.orderStatusTypeID !== eOrderStatusType.cancelled;
  }

  // This is very locked down, only for UBO and only for AwaitingExternalInformation State
  showCancelAttestationRequestButton(): boolean {
    return this.order.orderTypeID == eOrderType.ultimateBeneficialOwnershipReport &&
      (this.order.orderStatusTypeID == eOrderStatusType.awaitingExternalInformation
        || this.order.orderStatusTypeID == eOrderStatusType.externalErrorCorrection);
  }

  shouldShowExternalErrorCorrectionButton() {
    let isLessThan30DaysOld = new Date(this.order.orderStatusUpdatedDate!).getTime() > Date.now() - 30 * 24 * 60 * 60 * 1000;

    //This IF logic is in the BAPI endpoint, but since it's readily available might as well avoid an API call
    if (isLessThan30DaysOld &&
      (this.uiConfiguration.hasExternalErrorCorrections ?? false) &&
      (this.order.orderStatusTypeID == eOrderStatusType.complete ||
        this.order.orderStatusTypeID == eOrderStatusType.cancelled ||
        this.order.orderStatusTypeID == eOrderStatusType.externalErrorCorrection)) {
      this.orderStateRepo.isValidForExternalErrorCorrection(this.order.id)
        .subscribe(isValid => {
          this.isValidForExternalErrorCorrection = isValid;
        });
    }
    else {
      this.isValidForExternalErrorCorrection = false;
    }
  }

  isExternalErrorCorrection(): boolean {
    return this.order.orderStatusTypeID === eOrderStatusType.externalErrorCorrection;
  }

  isAwaitingExternalInformation(): boolean {
    return this.order.orderStatusTypeID === eOrderStatusType.awaitingExternalInformation;
  }

  showEditButton(): boolean {
    return this.hasDraftAccess(this.order.orderTypeID) && this.order.orderStatusTypeID === eOrderStatusType.historical;
  }

  showAmendmentButton(): boolean {
    return this.hasDraftAccess(eOrderType.amendment) && (this.uiConfiguration.hasAmendments ?? false) && !this.isExpired();
  }

  showRenewalButton(): boolean {
    return this.hasDraftAccess(eOrderType.renewal) && (this.uiConfiguration.hasRenewals ?? false) && !(this.order.lien?.isInfiniteTerm ?? false) && !this.isExpired();
  }

  showReregistrationButton(): boolean {
    return (this.uiConfiguration.hasReregistrations ?? false) &&
      (this.order.isLastOrder ?? false) &&
      (this.isDischarged() || this.isExpired()) &&
      this.isWithinReregistrationPeriod() &&
      this.hasDraftAccess(eOrderType.reregistration);
  }

  showDischargeButton(): boolean {
    return this.hasDraftAccess(eOrderType.discharge) && (this.uiConfiguration.hasDischarges ?? false) && !this.isExpired();
  }

  requestOrder(orderTypeID: eOrderType, order?: Order): boolean {
    this.orderManager.updateParentOrder(this.order);

    this.baseOrderDetails.patchValue({
      orderTypeID: orderTypeID
    });

    if (orderTypeID == eOrderType.pepsAndSanctions) {
      this.baseOrderDetails.patchValue({
        country: 'CA',
        jurisdiction: '_M'
      });
    }

    this.isSaving = true;

    // Otherwise, create it
    this.ordersRepo.createOrder("orders", order ?? this.baseOrderDetails.value)
      .subscribe(result => {
        this.isSaving = false;
        const order = (result as any).resource;
        this.orderManager.updateOrder(order);
        if (this.order.id) {
          this.router.navigate(['orders', 'ppsa-registration', this.order.id]);
        }
      });

    // do validation here
    return true;
  }

  linkToHubReport() {
    this.dialog.open(LinkToHubReportDialogComponent, {
      data: { order: this.order },
      minWidth: '500px'
    });
  }

  ultimateBeneficialOwnershipReport() {
    const dialogRef = this.dialog.open(UltimateBeneficialOwnershipDialogComponent, {
      data: { businessReportOrder: this.order, uboTreeNodeID: this.order.businessReportCriteria?.uboTreeNodeID, node: this.uboTreeNode },
      minWidth: '500px',
      maxWidth: '500px'
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result.event === 'submitted') {
        this.isSaving = true;
      }
    });
  }

  cancelAttestationRequest() {
    const dialogRef = this.dialog.open(ConfirmCancelComponent, {
      data: this.order,
      minWidth: '500px'
    });

    dialogRef.afterClosed().subscribe(shouldCancel => {
      if (shouldCancel) {
        this.cancelAttestation();
      }
    });
  }

  cancelAttestation() {
    const cancelledStatus = {
      newStatus: "Cancelled",
      status: "",
      message: "The RegHub user has cancelled their requested attestation."
    };

    this.orderStateRepo.updateOrderState(this.order.id, cancelledStatus)
      .subscribe({
        next: (result) => {
          this.order.orderStatusTypeID = "Cancelled";
          this.orderManager.updateOrder(this.order);
          this.messageService.success(this.transloco.translate('orders.attestationCancelled'));
        }
      });
  }

  correctAttestationConfirmation() {
    this.attestationTemplateRepositoryService.getAttestationTemplate(this.order.ultimateBeneficialOwnershipRequest!.templateID!)
      .subscribe(template => {
        if (template && template.cancelOnEfxDvsFailure && this.order.orderStatusTypeID == eOrderStatusType.cancelled) {
          const restartIdvStatus = {
            newStatus: "IdentityVerification",
            status: "",
            message: "Client has re-sent invitation email."
          };

          this.resendAttestation(restartIdvStatus, this.transloco.translate('orders.resendPrompts.restartIdv'));
        } else {
          const externalErrorCorrectionStatus = {
            newStatus: "ExternalErrorCorrection",
            status: "",
            message: "Client has re-sent invitation email."
          };

          this.resendAttestation(externalErrorCorrectionStatus, this.transloco.translate('orders.resendPrompts.errorCorrection'));
        }
      });
  }

  resendAttestationInvitation(status: string) {
    const request = {
      newStatus: status,
      status: "",
      message: "Client has re-sent invitation email."
    };

    this.checkIDVsPastExpiryDate(request);
  }

  resendAttestation(newStatus: any, message: string | null = null) {
    const dialogRef = this.dialog.open(ResendMessageDialog, { data: { message: message }, maxWidth: '700px', minWidth: '700px' });

    dialogRef.afterClosed().subscribe(async (result: { shouldResend: boolean; message: string | null; }) => {
      if (!result.shouldResend) {
        return;
      }

      this.isSaving = true;

      if (result.message) {
        const patch: PatchDocument[] = [
          {
            op: 'replace',
            path: '/messageToAttestor',
            value: result.message
          },
        ];

        await firstValueFrom(this.ultimateBeneficialOwnershipRequestService.patchUltimateBeneficialOwnershipRequest(this.order.id, this.order.ultimateBeneficialOwnershipRequest!.id, patch));
      }

      this.orderStateRepo.updateOrderState(this.order.id, newStatus)
        .subscribe({
          next: (result) => {
            this.order.orderStatusTypeID = newStatus.newStatus;
            this.orderManager.updateOrder(this.order);
            this.messageService.success(this.transloco.translate('orders.attestationResent'));
            this.isSaving = false;
          }
        });
    });
  }

  checkIDVsPastExpiryDate(newStatus: any) {
    // If a pending Entrust DVS source exists on the order longer than 30 days old
    if (this.order.identityVerificationDetails?.sources?.some(s => s.name === eIdentityVerificationSource.EntrustDvs && s.status === eIdentityVerificationResult.Pending) ?? false) {
      const diff = Math.abs(new Date(this.order.added).getTime() - new Date().getTime());
      const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
      if (diffDays >= 30) {
        this.resendAttestation(newStatus, this.transloco.translate('orders.resendPrompts.dvsExpired'));
      } else {
        // Check if an extension will put the date past the 30 day DVS limit
        const template = this.attestationTemplateRepositoryService.getAttestationTemplate(this.order.ultimateBeneficialOwnershipRequest!.templateID!)
          .subscribe(template => {
            if (template) {
              const diff = Math.abs(new Date(this.order.added).getTime() - (new Date().getTime() + (template.daysUntilInviteExpiration! * 1000 * 3600 * 24)));
              const diffDays = Math.ceil(diff / (1000 * 3600 * 24));
              if (diffDays >= 30) {
                const realExtensionDate = new Date(new Date(this.order.added).getTime() + (30 * 1000 * 3600 * 24));

                this.resendAttestation(newStatus, this.transloco.translate('orders.resendPrompts.extendLimit', { days: template.daysUntilInviteExpiration, date: realExtensionDate.toDateString() }));
              } else {
                this.resendAttestation(newStatus);
              }
            }
          });
      }
    } else if (this.order.identityVerificationDetails?.sources?.some(s => s.name === eIdentityVerificationSource.EfxDvs && s.status === eIdentityVerificationResult.Expired || s.status === eIdentityVerificationResult.Fail)) {
      this.resendAttestation(newStatus, this.transloco.translate('orders.resendPrompts.dvsExpired'));
    } else {
      this.resendAttestation(newStatus);
    }
  }

  setIsActionable() {
    if (this.order.orderTypeID === eOrderType.businessReport
      && this.order.orderStatusTypeID != eOrderStatusType.pending) {
      this.isActionable = this.order.searchResult?.resultState?.id !== eResultState.noResult;

      return;
    }

    if (this.order.orderTypeID === eOrderType.businessSearch) {
      this.isActionable = this.showDuplicateButton();

      return;
    }

    this.isActionable = (this.order.isLastOrder ?? false) &&

      (this.order.orderStatusTypeID === eOrderStatusType.complete ||
        this.order.orderStatusTypeID === eOrderStatusType.historical) &&

      (this.showDuplicateButton() ||
        this.showUpdateBusinessReportButton() ||
        this.showEditButton() ||
        this.showAmendmentButton() ||
        this.showRenewalButton() ||
        this.showReregistrationButton() ||
        this.showDischargeButton());
  }

  getExpiryDate(order: Order, registrationDate?: Date) {
    // If we have a requested expiry date use that
    if (order.lien?.expiryDate) {
      return new Date(order.lien.expiryDate);
    } else if (order.lien?.registrationResults?.at(0)?.expiryDate) {
      return new Date(order.lien.registrationResults.at(0)!.expiryDate!);
    } else if (order.referenceRegistrationInformation?.expiryDate && order.lien?.term) {
      return this.calculateExpiryDateFromTerm(order.referenceRegistrationInformation.expiryDate, order.lien.term);
    } else {
      // otherwise, calculate expiry date based on term
      return this.calculateExpiryDateFromTerm(registrationDate!, order.lien!.term!);
    }
  }

  calculateExpiryDateFromTerm(registrationDate: Date, term: number): Date {
    const expiryDate = new Date(registrationDate); // Clone the original date
    expiryDate.setFullYear(expiryDate.getFullYear() + term);
    return expiryDate;
  }

  isDischarged() {
    return this.order.orderGroup?.lastOrderType === eOrderType.discharge;
  }

  isExpired() {
    // guard against showing expired when we are actually infinite term
    if (this.order.lien?.isInfiniteTerm ?? false) {
      return false;
    }

    const today = new Date();
    const expiryDate = this.getExpiryDate(this.order, this.order.referenceRegistrationInformation?.originalRegistrationDate ?? undefined);

    // add a day because the date is essentially set for 00:00 of that day and needs to be 23:59 to be inclusive
    expiryDate.setDate(expiryDate.getDate() + 1);

    if (expiryDate) {
      return new Date(expiryDate).getTime() <= today.getTime();
    } else {
      return false;
    }
  }

  isWithinReregistrationPeriod() {
    if (this.order.jurisdiction === 'ON') {
      return true;
    }

    const today = new Date();
    const priorDate = new Date(new Date().setDate(today.getDate() - 30)).getTime();

    if (this.order.orderTypeID === eOrderType.discharge) {
      if (this.order.discharge?.dischargeDate) {
        return new Date(this.order.discharge!.dischargeDate).getTime() >= priorDate;
      } else {
        //then just catch in validation
        return true;
      }
    } else {
      const expiryDate = this.getExpiryDate(this.order, this.order.referenceRegistrationInformation?.originalRegistrationDate ?? undefined);
      if (expiryDate) {
        return new Date(expiryDate).getTime() >= priorDate;
      } else {
        return false;
      }
    }
  }

  hasDraftAccess(orderTypeID: eOrderType): boolean {
    return this.configuration.hasDraftAccess(orderTypeID);
  }

  showResendAttestationButton(): boolean {
    return this.order.orderTypeID === eOrderType.ultimateBeneficialOwnershipReport && (
      this.isExternalErrorCorrection() || this.isAwaitingExternalInformation()
    );
  }

  // Card wrapper for the attestation actions. Must stay the exact OR of the buttons
  // inside it, otherwise the card renders empty (e.g. a KYC in AwaitingExternalInformation).
  showAttestationActionsCard(): boolean {
    return (this.isValidForExternalErrorCorrection && !this.isExternalErrorCorrection()) ||
      this.showResendAttestationButton() ||
      (!this.isActionable && this.showCancelAttestationRequestButton()) ||
      this.shouldShowAttestoryHistoryButton();
  }

  showResendDvsButton(): boolean {
    return this.order.orderTypeID === eOrderType.knowYourCustomer &&
      (this.order.identityVerificationDetails?.sources?.some(s => s.name === eIdentityVerificationSource.EntrustDvs && s.status === eIdentityVerificationResult.Pending) ?? false) &&
      this.order.orderStatusTypeID === eOrderStatusType.awaitingExternalInformation;
  }

  resendDvs() {
    this.orderStateRepo.updateOrderState(this.order.id,
      {
        newStatus: eOrderStatusType.awaitingExternalInformation,
        status: "",
        message: "Client has re-sent entrust dvs email."
      })
      .subscribe({
        next: (result) => {
          this.order.orderStatusTypeID = eOrderStatusType.awaitingExternalInformation;
          this.orderManager.updateOrder(this.order);
          this.messageService.success(this.transloco.translate('orders.documentVerificationEmailResent'));
          this.isSaving = false;
        }
      });
  }

  cancelDocumentVerification() {
    this.orderStateRepo.updateOrderState(this.order.id,
      {
        newStatus: eOrderStatusType.cancelled,
        status: "",
        message: "Client has cancelled Document verification."
      })
      .subscribe({
        next: (result) => {
          this.order.orderStatusTypeID = eOrderStatusType.cancelled;
          this.orderManager.updateOrder(this.order);
          this.messageService.success(this.transloco.translate('orders.knowYourCustomerOrderCancelled'));
          this.isSaving = false;
        }
      });
  }

  shouldShowAttestoryHistoryButton(): boolean {
    return this.order.orderTypeID == eOrderType.ultimateBeneficialOwnershipReport;
  }

  openAttestoryHistoryDialog(): void {
    this.dialog.open(AttestorHistoryDialogComponent, {
      data: this.order.id,
      minWidth: '600px'
    });
  }
}
